import { useEffect, useRef } from "react";
import { connectToChild, connectToParent } from "penpal";
import { ChildMethods, ParentMethods } from "../types";
import Deferred from "promise-deferred";
import { AsyncMethodReturns } from "penpal/lib/types";
import { Account, User } from "@kei.fi/data";
import { Web3ConnectionType } from "@kei.fi/web3-connect";
import { KEIConnectMethod } from "../providers";

export type PromiseOrTypeMethods<T> = {
  [key in keyof T]: T[key] extends (...args: infer A) => Promise<infer U>
    ? (...args: A) => Promise<U> | U
    : T[key];
};

export const useConnectToChild = (
  iframe: HTMLIFrameElement,
  methods: PromiseOrTypeMethods<ParentMethods>,
) => {
  const methodsRef = useRef(methods);
  const childRef = useRef(new Deferred<AsyncMethodReturns<ChildMethods>>());
  methodsRef.current = methods;

  useEffect(() => {
    const connection = connectToChild<ChildMethods>({
      iframe,
      // Methods child is exposing to parent.
      methods: {
        show() {
          return methodsRef.current.show();
        },
        hide() {
          return methodsRef.current.hide();
        },
        updateUser(user: User | undefined) {
          return methodsRef.current.updateUser(user);
        },
        updateAccount(account: Account | undefined) {
          return methodsRef.current.updateAccount(account);
        },
        updateChainId(chainId: number) {
          return methodsRef.current.updateChainId(chainId);
        },
        updateIsSignerUnlocked(unlocked: boolean) {
          return methodsRef.current.updateIsSignerUnlocked(unlocked);
        },
        updateIsAuthenticated(authenticated: boolean) {
          return methodsRef.current.updateIsAuthenticated(authenticated);
        },
        updateIsTrustedDomain(authenticated: boolean) {
          return methodsRef.current.updateIsTrustedDomain(authenticated);
        },
        updateConnectMethod(connectionMethod: KEIConnectMethod) {
          return methodsRef.current.updateConnectMethod(connectionMethod);
        },
        ready() {
          return methodsRef.current.ready();
        },
        web3RequestSignature(data: string) {
          return methodsRef.current.web3RequestSignature(data);
        },
        updateWeb3ConnectionType(connectionType: Web3ConnectionType) {
          return methodsRef.current.updateWeb3ConnectionType(connectionType);
        },
      },
    });

    connection.promise.then((child) => {
      console.log("connect initialised");
      childRef.current.resolve(child);
    });

    return () => {
      connection.destroy();
    };
  }, []);

  return childRef.current.promise;
};
